/**
 * cas-5c81 — what `/` (and `/es`) shows, decided from auth READINESS, not
 * from `isAuthenticated` alone.
 *
 * The SSR server never sees the backend-origin `__session` cookie on
 * staging/prod (server/middleware/session-auth.ts is dormant), so the old page
 * server-rendered the signed-out start page for EVERY visitor, and the client
 * kept it on screen while readiness was still `pending` (`!isAuthenticated`).
 * A signed-in user watched the sign-up card for 0.3–4.6 s on every hard load
 * or new tab before their home appeared.
 *
 * Same split as the header (utils/header-identity-ssr.ts, cas-981b), same
 * signal (`decideSsrHeaderIdentity`):
 *
 *   SSR `anonymous` — nothing about the request suggests an identity (crawlers,
 *     first-time visitors). The HTML carries the full start page, so the public
 *     landing's SEO/first paint are unchanged. It is only ever UPGRADED to the
 *     home once readiness is `authenticated`.
 *   SSR `pending` — a plausible identity (returning-user hint cookie, session
 *     cookie, deferred verify, Firebase return). The HTML carries a neutral
 *     hold; after mount the ONE readiness state decides. The start page appears
 *     only when readiness is definitively `anonymous`.
 *   SSR `authenticated` — the server verified a user (only when the dormant
 *     session-auth handler is active). The HTML carries the home.
 *
 * Pure module: no Nuxt, no window, no store imports.
 */

import type { AuthReadiness } from './auth-readiness';
import type { SsrHeaderIdentity } from './header-identity-ssr';

/** What the server decided for this request. */
export type HomeSsrIdentity = SsrHeaderIdentity | 'authenticated';

/** Which surface `/` renders. */
export type HomeSurface = 'start' | 'home' | 'pending' | 'unavailable';

export interface HomeSurfaceInput {
  /** The page has mounted on the client. Before that, mirror the server HTML. */
  hydrated: boolean;
  /** The server's decision, shared through Nuxt state so both sides agree. */
  ssrIdentity: HomeSsrIdentity;
  /** The ONE readiness state from the auth store. */
  readiness: AuthReadiness;
}

const SSR_SURFACE: Record<HomeSsrIdentity, HomeSurface> = {
  anonymous: 'start',
  pending: 'pending',
  authenticated: 'home'
};

export function resolveHomeSurface(input: HomeSurfaceInput): HomeSurface {
  if (!input.hydrated) return SSR_SURFACE[input.ssrIdentity];
  if (input.readiness === 'authenticated') return 'home';
  if (input.readiness === 'anonymous') return 'start';
  // Readiness is `pending` or `unavailable`: not a definitive answer. Keep what
  // the server committed to when it committed to a real surface; never show a
  // plausibly signed-in visitor the signed-out start page on a "don't know".
  if (input.ssrIdentity !== 'pending') return SSR_SURFACE[input.ssrIdentity];
  return input.readiness === 'unavailable' ? 'unavailable' : 'pending';
}

/** Locale prefixes i18n adds to `/` (`prefix_except_default`: English has none). */
const HOME_LOCALE_PREFIXES = ['es'] as const;

/**
 * cas-2e97 — true when `path` is the signed-in home (`/` or `/es`), ignoring a
 * trailing slash. The marketing-consent invitation waits for this surface so it
 * never opens over the Create tour, Add credits, or a deep-linked post (Ben,
 * #gabber-internal 2026-10-10 01:07Z).
 */
export function isHomePath(path: string): boolean {
  const trimmed = path.replace(/\/+$/, '');
  if (trimmed === '') return true;
  return HOME_LOCALE_PREFIXES.some(prefix => trimmed === `/${prefix}`);
}
